import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const PeriodBufferContext = createContext();

export const usePeriodBuffer = () => {
  const context = useContext(PeriodBufferContext);
  if (!context) {
    throw new Error('usePeriodBuffer must be used within a PeriodBufferProvider');
  }
  return context;
};

export const PeriodBufferProvider = ({ children }) => {
  const [periodBuffers, setPeriodBuffers] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Fetch all period buffers
  const fetchPeriodBuffers = useCallback(async () => {
    // Don't fetch if already initialized
    if (initialized) {
      return periodBuffers;
    }

    try {
      setLoading(true);

      // First fetch all inventory periods
      const periodsRes = await axios.get('/inventory-periods', { withCredentials: true });
      const periods = periodsRes.data?.data || periodsRes.data || [];

      // Fetch buffers for all periods in parallel
      const bufferMap = new Map();
      const fetchPromises = periods.map(async (period) => {
        try {
          const metaRes = await axios.get(
            `/inventory-periods-meta/by-period-and-name/${period.id}/periodbuffer`,
            { withCredentials: true }
          );
          const metaData = metaRes.data?.data || metaRes.data;
          if (metaData && metaData.value) {
            return { periodId: period.id, value: Number(metaData.value) || 0 };
          }
        } catch (err) {
          // Silently ignore all errors (404 = no buffer, 500 = endpoint issue)
          // Just don't add this period to the map
          return null;
        }
        return null;
      });

      // Wait for all fetches to complete (some may return null on error)
      const results = await Promise.allSettled(fetchPromises);

      // Add successful results to the map
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          bufferMap.set(result.value.periodId, result.value.value);
        }
      });

      setPeriodBuffers(bufferMap);
      setInitialized(true);
      return bufferMap;
    } catch (err) {
      // Only log if it's not a 401 (unauthorized - user not logged in)
      if (err.response?.status !== 401) {
        console.error('Failed to fetch period buffers:', err);
      }
      // Even if there's an error, mark as initialized to prevent repeated attempts
      const emptyMap = new Map();
      setPeriodBuffers(emptyMap);
      setInitialized(true);
      return emptyMap;
    } finally {
      setLoading(false);
    }
  }, [initialized, periodBuffers]);

  // Update a specific period buffer
  const updatePeriodBuffer = useCallback((periodId, bufferHours) => {
    setPeriodBuffers(prev => {
      const newMap = new Map(prev);
      if (bufferHours > 0) {
        newMap.set(periodId, bufferHours);
      } else {
        newMap.delete(periodId);
      }
      return newMap;
    });
  }, []);

  // Delete a period buffer
  const deletePeriodBuffer = useCallback((periodId) => {
    setPeriodBuffers(prev => {
      const newMap = new Map(prev);
      newMap.delete(periodId);
      return newMap;
    });
  }, []);

  // Clear buffers on logout
  const clearBuffers = useCallback(() => {
    setPeriodBuffers(new Map());
    setInitialized(false);
  }, []);

  const value = {
    periodBuffers,
    loading,
    initialized,
    fetchPeriodBuffers,
    updatePeriodBuffer,
    deletePeriodBuffer,
    clearBuffers
  };

  return (
    <PeriodBufferContext.Provider value={value}>
      {children}
    </PeriodBufferContext.Provider>
  );
};
